import React, { useState, useEffect } from 'react';
import {
  DownOutlined,
  GlobalOutlined,
  MobileOutlined,
  FacebookOutlined,
  InstagramOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  MessageOutlined,
  EyeOutlined,
  PlusOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { Select, Input, Button, Switch } from 'antd';
import { message } from 'antd';

const { TextArea } = Input;

const MetaDestinationAdCopy = ({ campaignData, onUpdate, onNext }) => {
  const [destinationType, setDestinationType] = useState(campaignData.metaAds?.destinationType || 'website');
  const [websiteUrl, setWebsiteUrl] = useState(campaignData.metaAds?.websiteUrl || '');
  const [displayLink, setDisplayLink] = useState(campaignData.metaAds?.displayLink || '');
  const [browserAddon, setBrowserAddon] = useState(campaignData.metaAds?.browserAddon || 'none');

  const [primaryTexts, setPrimaryTexts] = useState(campaignData.metaAds?.primaryTexts || ['']);
  const [headlines, setHeadlines] = useState(campaignData.metaAds?.headlines || ['']);
  const [description, setDescription] = useState(campaignData.metaAds?.description || '');
  const [callToAction, setCallToAction] = useState(campaignData.metaAds?.callToAction || 'learn_more');
  const [advantageCreative, setAdvantageCreative] = useState(campaignData.metaAds?.advantageCreative || false);
  const [utmParameters, setUtmParameters] = useState(campaignData.metaAds?.utmParameters || {
    source: '',
    medium: '',
    campaign: '',
    term: '',
    content: ''
  });

  const [previewPlacement, setPreviewPlacement] = useState('facebook_feed');

  const destinationTypes = [
    { value: 'website', label: 'Website', icon: <GlobalOutlined /> },
    { value: 'instant_experience', label: 'Instant Experience', icon: <EyeOutlined /> },
    { value: 'mobile_app', label: 'Mobile App', icon: <MobileOutlined /> },
    { value: 'facebook_event', label: 'Facebook Event', icon: <FacebookOutlined /> },
    { value: 'messenger', label: 'Messenger', icon: <MessageOutlined /> },
    { value: 'whatsapp', label: 'WhatsApp', icon: <MessageOutlined /> },
  ];

  const browserAddons = [
    { value: 'none', label: 'None' },
    { value: 'whatsapp', label: 'WhatsApp button' },
    { value: 'call', label: 'Call button' },
    { value: 'messaging', label: 'Messaging apps' },
  ];

  const callToActions = [
    'learn_more', 'shop_now', 'sign_up', 'contact_us', 'download',
    'book_now', 'get_quote', 'get_directions', 'watch_more', 'subscribe'
  ];

  const previewPlacements = [
    { key: 'facebook_feed', label: 'Facebook Feed', icon: <FacebookOutlined /> },
    { key: 'instagram_feed', label: 'Instagram Feed', icon: <InstagramOutlined /> },
    { key: 'facebook_stories', label: 'Facebook Stories', icon: <FacebookOutlined /> },
    { key: 'instagram_stories', label: 'Instagram Stories', icon: <InstagramOutlined /> },
    { key: 'facebook_reels', label: 'Facebook Reels', icon: <VideoCameraOutlined /> },
    { key: 'instagram_reels', label: 'Instagram Reels', icon: <PlayCircleOutlined /> },
    { key: 'messenger', label: 'Messenger', icon: <MessageOutlined /> },
    { key: 'audience_network', label: 'Audience Network', icon: <GlobalOutlined /> },
  ];

  const addTextVariation = (type) => {
    if (type === 'primary' && primaryTexts.length < 5) {
      setPrimaryTexts([...primaryTexts, '']);
    } else if (type === 'headline' && headlines.length < 5) {
      setHeadlines([...headlines, '']);
    }
  };

  const updateTextVariation = (type, index, value) => {
    if (type === 'primary') {
      const updated = [...primaryTexts];
      updated[index] = value;
      setPrimaryTexts(updated);
    } else if (type === 'headline') {
      const updated = [...headlines];
      updated[index] = value;
      setHeadlines(updated);
    }
  };

  const removeTextVariation = (type, index) => {
    if (type === 'primary' && primaryTexts.length > 1) {
      setPrimaryTexts(primaryTexts.filter((_, i) => i !== index));
    } else if (type === 'headline' && headlines.length > 1) {
      setHeadlines(headlines.filter((_, i) => i !== index));
    }
  };

  const validateCurrentStep = () => {
    if (destinationType === 'website' && !websiteUrl.trim()) {
      message.warning('Please enter a website URL');
      return false;
    }

    if (primaryTexts.every(text => !text.trim())) {
      message.warning('Please enter at least one primary text');
      return false;
    }

    if (headlines.every(text => !text.trim())) {
      message.warning('Please enter at least one headline');
      return false;
    }

    return true;
  };

  // Keep parent data in sync as fields change (single-page campaign view)
  useEffect(() => {
    onUpdate({
      metaAds: {
        destinationType,
        websiteUrl,
        displayLink,
        browserAddon,
        primaryTexts: primaryTexts.filter(text => text.trim()),
        headlines: headlines.filter(text => text.trim()),
        description,
        callToAction,
        advantageCreative,
        utmParameters,
      }
    });
  }, [destinationType, websiteUrl, displayLink, browserAddon, primaryTexts, headlines, description, callToAction, advantageCreative, utmParameters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    if (!validateCurrentStep()) {
      return;
    }

    onUpdate({
      metaAds: {
        ...campaignData.metaAds,
        destinationType,
        websiteUrl,
        displayLink,
        browserAddon,
        primaryTexts: primaryTexts.filter(text => text.trim()),
        headlines: headlines.filter(text => text.trim()),
        description,
        callToAction,
        advantageCreative,
        utmParameters,
      }
    });
    onNext();
  };

  const renderDestinationSection = () => (
    <div>

      {/* Destination Type */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          Destination Type
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {destinationTypes.map(type => (
            <div
              key={type.value}
              onClick={() => setDestinationType(type.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: destinationType === type.value ? '#f0f9ff' : '#fff',
                border: destinationType === type.value ? '2px solid var(--ink)' : '1px solid var(--paper-300)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: destinationType === type.value ? 'var(--ink)' : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: destinationType === type.value ? '#fff' : 'var(--ink-200)',
              }}>
                {type.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                  {type.label}
                </div>
              </div>
              {destinationType === type.value && (
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: 'var(--ink)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 'bold',
                }}>
                  ✓
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Website URL */}
      {destinationType === 'website' && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Website URL <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Input
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://yourwebsite.com"
            size="large"
          />
        </div>
      )}

      {/* Display Link */}
      {destinationType === 'website' && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Display Link (Optional)
          </label>
          <Input
            value={displayLink}
            onChange={(e) => setDisplayLink(e.target.value)}
            placeholder="Customize the URL shown in your ad"
            size="large"
          />
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '4px' }}>
            Leave blank to show your actual URL
          </div>
        </div>
      )}

      {/* Browser Add-on */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          Browser Add-on / Contact Method
        </label>
        <Select
          value={browserAddon}
          onChange={setBrowserAddon}
          style={{ width: '100%' }}
          size="large"
          options={browserAddons}
        />
        <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '4px' }}>
          Add contact buttons that appear when people visit your site
        </div>
      </div>
    </div>
  );

  const renderAdCopySection = () => (
    <div>

      {/* Primary Text */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
            Primary Text <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={() => addTextVariation('primary')}
            disabled={primaryTexts.length >= 5}
            size="small"
          >
            Add Variation
          </Button>
        </div>
        {primaryTexts.map((text, index) => (
          <div key={index} style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <TextArea
                value={text}
                onChange={(e) => updateTextVariation('primary', index, e.target.value)}
                placeholder="Write your primary ad text..."
                maxLength={125}
                rows={3}
                style={{ flex: 1 }}
              />
              {primaryTexts.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeTextVariation('primary', index)}
                  size="small"
                />
              )}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--ink-200)', marginTop: '4px' }}>
              {text.length}/125 characters • Variation {index + 1}
            </div>
          </div>
        ))}
      </div>

      {/* Headline */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
            Headline <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={() => addTextVariation('headline')}
            disabled={headlines.length >= 5}
            size="small"
          >
            Add Variation
          </Button>
        </div>
        {headlines.map((text, index) => (
          <div key={index} style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Input
                value={text}
                onChange={(e) => updateTextVariation('headline', index, e.target.value)}
                placeholder="Write your headline..."
                maxLength={40}
                style={{ flex: 1 }}
              />
              {headlines.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeTextVariation('headline', index)}
                  size="small"
                />
              )}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--ink-200)', marginTop: '4px' }}>
              {text.length}/40 characters • Variation {index + 1}
            </div>
          </div>
        ))}
      </div>

      {/* Description */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          Description (Optional)
        </label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a description..."
          maxLength={30}
        />
        <div style={{ fontSize: '10px', color: 'var(--ink-200)', marginTop: '4px' }}>
          {description.length}/30 characters
        </div>
      </div>

      {/* Call to Action */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          Call to Action
        </label>
        <Select
          value={callToAction}
          onChange={setCallToAction}
          style={{ width: '100%' }}
          size="large"
        >
          {callToActions.map(cta => (
            <Select.Option key={cta} value={cta}>
              {cta.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Select.Option>
          ))}
        </Select>
      </div>

      {/* Advantage+ Creative */}
      <div style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              background: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <ThunderboltOutlined style={{ fontSize: '16px', color: 'var(--ink-200)' }} />
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '2px' }}>
                Advantage+ Creative
              </div>
              <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>
                Automatically optimize your creative with AI enhancements
              </div>
            </div>
          </div>
          <Switch
            checked={advantageCreative}
            onChange={setAdvantageCreative}
            style={{
              background: advantageCreative ? 'var(--ink)' : 'var(--paper-300)',
            }}
          />
        </div>
        {advantageCreative && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--ink-200)' }}>
            ✓ Auto brightness & contrast • ✓ Auto music • ✓ Text overlay enhancements • ✓ Image/video variations
          </div>
        )}
      </div>

      {/* UTM Parameters */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          UTM / Tracking Parameters (Optional)
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <Input
            placeholder="Source (e.g., facebook)"
            value={utmParameters.source}
            onChange={(e) => setUtmParameters({...utmParameters, source: e.target.value})}
            size="large"
          />
          <Input
            placeholder="Medium (e.g., cpc)"
            value={utmParameters.medium}
            onChange={(e) => setUtmParameters({...utmParameters, medium: e.target.value})}
            size="large"
          />
          <Input
            placeholder="Campaign"
            value={utmParameters.campaign}
            onChange={(e) => setUtmParameters({...utmParameters, campaign: e.target.value})}
            size="large"
          />
          <Input
            placeholder="Term (optional)"
            value={utmParameters.term}
            onChange={(e) => setUtmParameters({...utmParameters, term: e.target.value})}
            size="large"
          />
          <Input
            placeholder="Content (optional)"
            value={utmParameters.content}
            onChange={(e) => setUtmParameters({...utmParameters, content: e.target.value})}
            size="large"
          />
        </div>
      </div>
    </div>
  );

  const renderAdPreview = () => (
    <div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Preview Placement
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
            {previewPlacements.map(placement => (
              <Button
                key={placement.key}
                type={previewPlacement === placement.key ? 'primary' : 'default'}
                icon={placement.icon}
                onClick={() => setPreviewPlacement(placement.key)}
                size="small"
              >
                {placement.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Mock Ad Preview */}
      <div style={{
        border: '1px solid var(--paper-300)',
        borderRadius: '8px',
        padding: '16px',
        background: '#fff',
        minHeight: '300px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center', color: 'var(--ink-200)' }}>
          <EyeOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            Ad Preview - {previewPlacements.find(p => p.key === previewPlacement)?.label}
          </div>
          <div style={{ fontSize: '14px' }}>
            This would show a real-time preview of your ad in the selected placement
          </div>
          <div style={{ fontSize: '12px', marginTop: '8px', color: '#9ca3af' }}>
            Preview functionality would integrate with Meta's Ad Preview API
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="step-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px 100px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
        }}>
          <LinkOutlined style={{ fontSize: '32px', color: '#111827' }} />
        </div>

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: '#000',
          marginBottom: '12px',
          textAlign: 'center'
        }}>
          Delivery & Ad Copy
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'var(--ink-200)',
          marginBottom: 0,
          textAlign: 'center'
        }}>
          Set your destination and create compelling ad copy
        </p>
      </div>

      {/* Main Content — 3 columns side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', alignItems: 'start' }}>

        {/* Column 1: Destination */}
        <div style={{ background: '#fff', border: '1px solid var(--paper-300)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LinkOutlined style={{ fontSize: '18px', color: 'var(--ink)' }} />
            </div>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#111827', margin: 0 }}>Destination</h3>
          </div>
          {renderDestinationSection()}
        </div>

        {/* Column 2: Ad Copy */}
        <div style={{ background: '#fff', border: '1px solid var(--paper-300)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ThunderboltOutlined style={{ fontSize: '18px', color: '#10b981' }} />
            </div>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#111827', margin: 0 }}>Ad Copy</h3>
          </div>
          {renderAdCopySection()}
        </div>

        {/* Column 3: Ad Preview */}
        <div style={{ background: '#fff', border: '1px solid var(--paper-300)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#fdf4ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EyeOutlined style={{ fontSize: '18px', color: '#a855f7' }} />
            </div>
            <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#111827', margin: 0 }}>Ad Preview</h3>
          </div>
          {renderAdPreview()}
        </div>

      </div>

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid var(--paper-300)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10
      }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
          Building: Destination & Ad Copy
        </div>
        <button
          onClick={handleContinue}
          style={{
            padding: '12px 32px',
            background: 'var(--ink-200)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#4b5563';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--ink-200)';
          }}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default MetaDestinationAdCopy;
